// app/frontend/components/inbox/MessageAttachment.tsx

import { cn } from '@/lib/utils'
import { FileText, Download } from 'lucide-react'
import type { MessageAttachment as AttachmentType } from '@/types/serializers'
import { ImageLightbox } from '@/components/ui/ImageLightbox'

interface MessageAttachmentProps {
  attachment: AttachmentType
  isFromAgent: boolean
  className?: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function MessageAttachment({ attachment, isFromAgent, className }: MessageAttachmentProps) {
  const isImage = attachment.content_type.startsWith('image/')

  if (isImage) {
    return (
      <ImageLightbox
        src={attachment.url}
        thumbnailSrc={attachment.thumbnail_url || undefined}
        alt={attachment.filename}
        className={className}
      />
    )
  }

  // Document attachment
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-2 p-2 rounded-lg mt-2",
        "hover:bg-muted/50 transition-colors",
        isFromAgent ? "bg-primary/10" : "bg-muted",
        className
      )}
    >
      <FileText className="size-5 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          {attachment.filename}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatFileSize(attachment.byte_size)}
        </div>
      </div>
      <Download className="size-4 text-muted-foreground" />
    </a>
  )
}

export default MessageAttachment
